import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, addMonths, subMonths } from 'date-fns';
import { 
  Calendar, ChevronLeft, ChevronRight, Clock, CheckCircle2, 
  Circle, Phone, Mail, Users, X, ChevronDown, ChevronUp,
  Plus, Loader2, RefreshCw
} from 'lucide-react';
import { useRealtimeNotifications } from '@/hooks/useRealtimeNotifications';

// Collapsible Section wrapper
function CollapsibleSection({ title, count, defaultOpen = true, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-slate-200 dark:border-slate-700" data-testid={testId}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        data-testid={`${testId}-toggle`}
      >
        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
          {title}
          {count !== undefined && (
            <span className="ml-1.5 px-1.5 py-0.5 text-[10px] bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-full">{count}</span>
          )}
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
      </button>
      {open && <div className="px-4 py-3">{children}</div>}
    </div>
  );
}

// Quick Add Task Modal
function QuickAddTaskModal({ isOpen, onClose, onTaskCreated, presetDate }) {
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [taskType, setTaskType] = useState('follow_up');
  const [dueTime, setDueTime] = useState('10:00');
  const token = localStorage.getItem('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setLoading(true);
    try {
      const dateStr = presetDate ? format(presetDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
      await axios.post(`${API}/tasks`, {
        title: title.trim(),
        task_type: taskType,
        due_date: `${dateStr}T${dueTime}:00`,
        status: 'not_started',
        priority: 'medium'
      }, { headers: { Authorization: `Bearer ${token}` } });
      setTitle('');
      onTaskCreated && onTaskCreated();
      onClose();
    } catch (error) {
      console.error('Error creating task:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-sm" data-testid="quick-add-task-modal">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="font-semibold text-slate-900 dark:text-white">Add Task</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Title *</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Enter task title..." className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500" autoFocus required data-testid="quick-task-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Type</label>
              <select value={taskType} onChange={(e) => setTaskType(e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                <option value="call">Call</option><option value="follow_up">Follow-up</option><option value="meeting">Meeting</option><option value="email">Email</option><option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Time</label>
              <input type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)} className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition">Cancel</button>
            <button type="submit" disabled={loading || !title.trim()} className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg hover:bg-slate-800 dark:hover:bg-slate-200 disabled:opacity-50 flex items-center gap-2 transition" data-testid="quick-task-submit">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Add Task
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EmailCalendarPanel({ isOpen, onToggle }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const token = localStorage.getItem('token');

  // Microsoft connection state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [microsoftConnected, setMicrosoftConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [ssoLogin, setSsoLogin] = useState(false);

  const fetchCalendarData = useCallback(async () => {
    try {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      const response = await axios.get(`${API}/tasks/calendar-data?month=${month}&year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarData(response.data);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    }
  }, [currentMonth, token]);

  const fetchAllTasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tasks?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllTasks(response.data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isOpen) {
      checkMicrosoftConnection();
      fetchCalendarData();
      fetchAllTasks();
    }
  }, [isOpen, fetchCalendarData, fetchAllTasks]);

  const refreshAll = () => { fetchCalendarData(); fetchAllTasks(); };

  // Real-time push: Graph calendar change → refresh this panel's data.
  // Only acts when the panel is actually open to avoid background work.
  useRealtimeNotifications((evt) => {
    if (!isOpen) return;
    if (evt?.type === 'graph_change' && evt?.kind === 'calendar') {
      fetchCalendarData();
      fetchAllTasks();
    }
  });

  const checkMicrosoftConnection = async () => {
    try {
      const response = await axios.get(`${API}/auth/microsoft/connection-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMicrosoftConnected(response.data.connected);
      setSsoLogin(response.data.sso_login || false);
    } catch (error) { /* ignore */ }
  };

  const handleConnectMicrosoft = async () => {
    setConnecting(true);
    try {
      const response = await axios.get(`${API}/auth/microsoft/connect-url?redirect_uri=${encodeURIComponent(window.location.origin + '/auth/microsoft/callback')}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const width = 600, height = 700;
      const left = (window.screen.width - width) / 2, top = (window.screen.height - height) / 2;
      const popup = window.open(response.data.auth_url, 'Microsoft Login', `width=${width},height=${height},left=${left},top=${top}`);
      const handleMessage = async (event) => {
        if (event.data?.type === 'MICROSOFT_AUTH_SUCCESS') {
          window.removeEventListener('message', handleMessage);
          popup?.close();
          setMicrosoftConnected(true);
          setSyncStatus({ type: 'success', message: 'Microsoft account connected!' });
          setTimeout(() => setSyncStatus(null), 3000);
        } else if (event.data?.type === 'MICROSOFT_AUTH_ERROR') {
          window.removeEventListener('message', handleMessage);
          popup?.close();
          setSyncStatus({ type: 'error', message: event.data.message || 'Connection failed' });
          setTimeout(() => setSyncStatus(null), 3000);
        }
      };
      window.addEventListener('message', handleMessage);
      const checkClosed = setInterval(() => {
        if (popup?.closed) { clearInterval(checkClosed); setConnecting(false); checkMicrosoftConnection(); }
      }, 500);
    } catch (error) {
      setSyncStatus({ type: 'error', message: error.response?.data?.detail || 'Failed to connect' });
      setTimeout(() => setSyncStatus(null), 3000);
    } finally {
      setConnecting(false);
    }
  };

  const handleSyncFromOutlook = async () => {
    if (!microsoftConnected) {
      setSyncStatus({ type: 'error', message: 'Connect Microsoft account first' });
      setTimeout(() => setSyncStatus(null), 3000);
      return;
    }
    setSyncing(true);
    try {
      await axios.post(`${API}/microsoft-sync/sync-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSyncStatus({ type: 'success', message: 'Sync started!' });
      setTimeout(() => { refreshAll(); setSyncStatus(null); }, 2000);
    } catch (error) {
      setSyncStatus({ type: 'error', message: error.response?.data?.detail || 'Sync failed' });
      setTimeout(() => setSyncStatus(null), 3000);
    } finally {
      setSyncing(false);
    }
  };

  // Calendar helpers
  const daysInMonth = eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) });
  const startDay = startOfMonth(currentMonth).getDay();
  const emptyDays = Array(startDay).fill(null);

  const getEventInfo = (date) => {
    if (!calendarData?.dates_with_events) return null;
    const dateKey = format(date, 'yyyy-MM-dd');
    return calendarData.dates_with_events[dateKey] || null;
  };

  // Tasks for selected date
  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
  const tasksForDate = allTasks.filter(t => {
    if (!t.due_date) return false;
    return t.due_date.startsWith(selectedDateStr);
  });

  const meetingsForDate = tasksForDate.filter(t => t.task_type === 'meeting' || t.type === 'meeting');
  const otherTasksForDate = tasksForDate.filter(t => t.task_type !== 'meeting' && t.type !== 'meeting');

  // Upcoming meetings (next 7 from today)
  const now = new Date();
  const upcomingMeetings = allTasks
    .filter(t => {
      if (t.status === 'done') return false;
      if (!t.due_date) return false;
      const d = new Date(t.due_date);
      return d >= now && (t.task_type === 'meeting' || t.type === 'meeting');
    })
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
    .slice(0, 7);

  // Upcoming non-meeting tasks
  const upcomingTasks = allTasks
    .filter(t => {
      if (t.status === 'done') return false;
      if (!t.due_date) return false;
      return t.task_type !== 'meeting' && t.type !== 'meeting';
    })
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
    .slice(0, 10);

  const handleMarkComplete = async (taskId) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status: 'done' }, { headers: { Authorization: `Bearer ${token}` } });
      refreshAll();
    } catch (error) { console.error('Error completing task:', error); }
  };

  const getTaskIcon = (type) => {
    switch (type) {
      case 'call': return <Phone className="w-3 h-3" />;
      case 'email': return <Mail className="w-3 h-3" />;
      case 'meeting': return <Users className="w-3 h-3" />;
      default: return <Circle className="w-3 h-3" />;
    }
  };

  if (!isOpen) {
    return (
      <button onClick={onToggle} className="fixed right-0 top-1/2 -translate-y-1/2 bg-slate-900 text-white p-2 rounded-l-lg shadow-lg hover:bg-slate-800 transition z-40" data-testid="open-calendar-panel">
        <Calendar className="w-5 h-5" />
      </button>
    );
  }

  return (
    <div className="w-96 bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 flex flex-col h-full overflow-hidden" data-testid="email-calendar-panel">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-semibold text-slate-800 dark:text-white">Calendar</span>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={handleSyncFromOutlook} disabled={syncing} className="p-1.5 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition" title="Sync from Microsoft 365" data-testid="sync-outlook-btn">
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={onToggle} className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {syncStatus && (
          <div className={`mt-2 px-2 py-1.5 rounded text-xs ${syncStatus.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'}`}>
            {syncStatus.message}
          </div>
        )}

        {!microsoftConnected && !ssoLogin && (
          <button onClick={handleConnectMicrosoft} disabled={connecting} className="mt-2 w-full px-3 py-2 bg-slate-900 dark:bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-medium rounded-lg hover:bg-slate-800 dark:hover:bg-slate-800 dark:hover:bg-white disabled:opacity-50 flex items-center justify-center gap-2 transition" data-testid="connect-microsoft-btn">
            {connecting ? <RefreshCw className="w-3 h-3 animate-spin" /> : (
              <svg className="w-3 h-3" viewBox="0 0 21 21" fill="currentColor"><path d="M0 0h10v10H0V0zm11 0h10v10H11V0zM0 11h10v10H0V11zm11 0h10v10H11V11z"/></svg>
            )}
            {connecting ? 'Connecting...' : 'Connect Microsoft 365'}
          </button>
        )}

        {microsoftConnected && (
          <div className="mt-2 px-2 py-1.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded text-xs flex items-center gap-1.5">
            <CheckCircle2 className="w-3 h-3" /> Microsoft 365 connected
          </div>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Mini Calendar */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded text-slate-500 dark:text-slate-400"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm font-semibold text-slate-800 dark:text-white">{format(currentMonth, 'MMMM yyyy')}</span>
            <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded text-slate-500 dark:text-slate-400"><ChevronRight className="w-4 h-4" /></button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
              <div key={i} className="text-center text-[10px] font-medium text-slate-400 dark:text-slate-500 py-0.5">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {emptyDays.map((_, i) => <div key={`e-${i}`} className="h-8" />)}
            {daysInMonth.map((date) => {
              const eventInfo = getEventInfo(date);
              const today = isToday(date);
              const isSelected = isSameDay(date, selectedDate);
              const hasEvents = eventInfo && (eventInfo.tasks > 0 || eventInfo.meetings > 0 || eventInfo.reminders > 0);

              return (
                <button
                  key={date.toISOString()}
                  onClick={() => setSelectedDate(date)}
                  className={`h-8 flex flex-col items-center justify-center rounded-lg text-xs relative transition
                    ${today && isSelected ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 font-bold ring-2 ring-blue-300 dark:ring-blue-500' :
                      today ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 font-bold' :
                      isSelected ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 font-bold' :
                      'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'}
                    ${hasEvents && !today && !isSelected ? 'font-semibold' : ''}
                  `}
                  data-testid={`cal-day-${format(date, 'yyyy-MM-dd')}`}
                >
                  {date.getDate()}
                  {hasEvents && (
                    <div className="absolute bottom-0.5 flex gap-0.5">
                      <div className={`w-1 h-1 rounded-full ${today || isSelected ? 'bg-white dark:bg-slate-900' : 'bg-blue-500'}`} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Date's Events */}
        <div className="border-t border-slate-200 dark:border-slate-700 px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-slate-800 dark:text-white">
              {isSameDay(selectedDate, new Date()) ? 'Today' : format(selectedDate, 'EEE, MMM d')}
            </h3>
            <span className="text-[10px] text-slate-400">{tasksForDate.length} items</span>
          </div>

          {meetingsForDate.length > 0 && (
            <div className="mb-2">
              <p className="text-[10px] font-medium text-slate-400 dark:text-slate-500 uppercase mb-1">Meetings</p>
              <div className="space-y-1.5">
                {meetingsForDate.map(m => (
                  <div key={m.id} className="flex items-center gap-2 px-2 py-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg" data-testid={`meeting-${m.id}`}>
                    <Users className="w-3 h-3 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-800 dark:text-white truncate">{m.title}</p>
                      {m.due_date && <p className="text-[10px] text-slate-500 dark:text-slate-400">{format(new Date(m.due_date), 'h:mm a')}</p>}
                    </div>
                    {m.lead_name && <span className="text-[10px] text-blue-600 dark:text-blue-400 truncate max-w-[60px]">{m.lead_name}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {otherTasksForDate.length > 0 && (
            <div className="space-y-1">
              {otherTasksForDate.map(t => (
                <div key={t.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition">
                  <button onClick={() => handleMarkComplete(t.id)} className={`flex-shrink-0 ${t.status === 'done' ? 'text-green-500' : 'text-slate-300 dark:text-slate-600 hover:text-blue-500'}`}>
                    {t.status === 'done' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs truncate ${t.status === 'done' ? 'text-slate-400 line-through' : 'text-slate-800 dark:text-white'}`}>{t.title}</p>
                  </div>
                  <span className="text-[10px] text-slate-400">{getTaskIcon(t.task_type || t.type)}</span>
                </div>
              ))}
            </div>
          )}

          {tasksForDate.length === 0 && (
            <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-3">No events for this date</p>
          )}

          <button
            onClick={() => setShowQuickAddModal(true)}
            className="w-full mt-2 py-1.5 border border-dashed border-slate-300 dark:border-slate-600 rounded-lg text-xs text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-400 dark:hover:border-blue-500 transition flex items-center justify-center gap-1"
            data-testid="add-task-date-btn"
          >
            <Plus className="w-3 h-3" /> Add task
          </button>
        </div>

        {/* Upcoming Meetings - Collapsible */}
        <CollapsibleSection title="Upcoming Meetings" count={upcomingMeetings.length} defaultOpen={false} testId="upcoming-meetings-section">
          {upcomingMeetings.length === 0 ? (
            <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-2">No upcoming meetings</p>
          ) : (
            <div className="space-y-2">
              {upcomingMeetings.map(m => (
                <div key={m.id} className="flex items-center gap-2" data-testid={`upcoming-meeting-${m.id}`}>
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{m.title}</p>
                    {m.lead_name && <p className="text-[10px] text-blue-500 dark:text-blue-400 truncate">{m.lead_name}</p>}
                  </div>
                  <span className="text-[10px] text-slate-400 dark:text-slate-500 whitespace-nowrap">
                    {m.due_date ? format(new Date(m.due_date), 'MMM d, h:mm a') : '-'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>

        {/* Tasks - Collapsible */}
        <CollapsibleSection title="Tasks" count={upcomingTasks.length} defaultOpen={false} testId="tasks-section">
          {upcomingTasks.length === 0 ? (
            <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-2">No pending tasks</p>
          ) : (
            <div className="space-y-1.5">
              {upcomingTasks.map(t => (
                <div key={t.id} className="flex items-center gap-2">
                  <button onClick={() => handleMarkComplete(t.id)} className={`flex-shrink-0 ${t.status === 'done' ? 'text-green-500' : 'text-slate-300 dark:text-slate-600 hover:text-blue-500'}`}>
                    {t.status === 'done' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs truncate ${t.status === 'done' ? 'text-slate-400 line-through' : 'text-slate-700 dark:text-slate-200'}`}>{t.title}</p>
                    {t.lead_name && <p className="text-[10px] text-blue-500 truncate">{t.lead_name}</p>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-[10px] text-slate-400">{getTaskIcon(t.task_type || t.type)}</span>
                    <span className="text-[10px] text-slate-400 dark:text-slate-500">{t.due_date ? format(new Date(t.due_date), 'MMM d') : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>
      </div>

      {/* Quick Add Task Modal */}
      <QuickAddTaskModal
        isOpen={showQuickAddModal}
        onClose={() => setShowQuickAddModal(false)}
        onTaskCreated={refreshAll}
        presetDate={selectedDate}
      />
    </div>
  );
}

export default EmailCalendarPanel;
